// Copyright (c) Docugami, Inc. All rights reserved.

import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useExpandMutation } from "store/repeatsSlice";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectClusterId } from "store/reports/selectors";
import { DgmlInstancesSingleton } from "./documentDgml";
import { createFolderIdString } from "components/file_management/file_helpers";
import { Chunk } from "store/dgml/dgmlTypes";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { removePreviewChunkSet, setPreviewChunks } from "store/sdvSlice";
import { machineGreen } from "components/shell/presentation/AppStyles";

export const useRepeatsExpansion = (
    fileId: string,
    spanIds?: string[],
    negativeSpanIds?: string[],
    highlightResults?: boolean): Chunk[] | undefined => {
    const docsetId = useAppSelector(selectClusterId);
    const dispatch = useAppDispatch();
    const [expandResults, setExpandResults] = useState<string[]>([]);

    const [triggerExpand, { data: rawExpandResults, reset: resetExpandResults, isLoading }] = useExpandMutation();
    useEffect(() => {
        if (!isLoading && rawExpandResults !== undefined) {
            setExpandResults(x => {
                const newValues = !spanIds?.length ? [] : rawExpandResults.results.filter(x => !spanIds.includes(x.value)).map(x => x.value);
                if (newValues.length !== x.length || newValues.some(value => !x.includes(value))) {
                    return newValues;
                }

                return x;
            })
        }
    }, [rawExpandResults, isLoading, spanIds])

    const triggerExpandDebounce = useMemo(() => debounce(triggerExpand, 1000, { trailing: true, leading: true, maxWait: 1000 }), [triggerExpand]);

    const [chunks, isSupported] = useMemo(() => {
        const chunks = spanIds
            ?.map(spanId => DgmlInstancesSingleton.getInstanceById(createFolderIdString(docsetId, fileId), spanId)?.chunk)
            .filter(chunk => !!chunk) as Chunk[] | undefined

        let isSupported = false;
        if (!!chunks && chunks.length > 2) {
            const targetLength = TokenRangeUtils.getLength(chunks[0].tokenRange);
            isSupported = chunks.every(chunk => TokenRangeUtils.getLength(chunk.tokenRange) === targetLength);
        }

        return [chunks, isSupported];
    }, [docsetId, fileId, spanIds]);

    useEffect(() => {
        if (!!chunks && isSupported) {
            triggerExpandDebounce({
                docsetId: docsetId,
                fileId: fileId,
                positiveSpans: chunks.map(x => x.spanId),
                negativeSpans: negativeSpanIds ?? [],
            })
        }
    }, [chunks, isSupported, fileId, docsetId, negativeSpanIds, triggerExpandDebounce]);

    useEffect(() => {
        if (!isSupported) {
            resetExpandResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSupported])

    const results = useMemo<Chunk[]>(() => (expandResults
        ?.map(x => DgmlInstancesSingleton.getInstanceById(createFolderIdString(docsetId, fileId), x)?.chunk)
        .filter(chunk => !!chunk) ?? []) as Chunk[],
        [expandResults, docsetId, fileId]);

    useEffect(() => {
        if (!!results.length && highlightResults) {
            dispatch(setPreviewChunks({
                id: 'repeats-expansion',
                chunks: {
                    spanIds: results.map(x => x.spanId),
                    color: machineGreen
                }
            }))
        }
        else {
            dispatch(removePreviewChunkSet('repeats-expansion'));
        }
    }, [results, highlightResults, dispatch])

    return isSupported ? results : undefined;
}
